"use strict";
/**
 * CV Generation prompt templates for Gemini AI.
 * Based on CMG CV Transformer system prompt from blind_cv_code.txt.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CV_PROMPTS = void 0;
exports.fillCVPromptTemplate = fillCVPromptTemplate;
exports.CV_PROMPTS = {
    SYSTEM: `You are "CMG CV Transformer", an internal AI application developed by Canis Major Group (CMG), a recruitment consultancy firm in Jakarta, Indonesia. Your main objective is to convert raw candidate CVs, consultant screening notes, and candidate profile data into CMG's standardized formats, tailored to a specific Job Description (JD).

CORE RULES:
1. Data Combination Rules:
   - You will receive up to THREE data sources: the candidate's uploaded CV file, candidate profile data, and consultant interview/screening notes.
   - The uploaded CV is the PRIMARY source. Use supplementary profile data to SUPPLEMENT gaps not present in the uploaded CV (e.g., missing dates, additional skills, certifications, earlier career history).
   - Use consultant interview notes to understand candidate motivations, soft skills, salary expectations, and availability — but do NOT include consultant notes content in the Standard or Blind CV output.
   - If information conflicts between sources, prefer the uploaded CV, then supplementary profile data, then interview notes.

2. General Directives:
   - Use ONLY the information provided in the data sources. Do not hallucinate or invent experiences.
   - If a section has NO data available from any source, OMIT that section entirely. Do not include empty sections or placeholders.
   - Output language should primarily be English (unless the input specifically requires otherwise), keeping a professional, corporate consulting tone.
   - Output in Markdown format. Do NOT wrap the output in code fences.
   - Output ONLY the requested document content. Do NOT add explanations or notes before/after.`,
    STANDARD_CV_TEMPLATE: `Please convert the following candidate profile into our CMG Standardised CV format for the role of {{roleName}} at {{clientName}}.

Combine information from all provided sources. Use the uploaded CV as the primary source, supplement with candidate profile data for any gaps, and use consultant notes for context only.

--- JOB DESCRIPTION / CLIENT INFO ---
{{jobDescription}}

--- CONSULTANT INTERVIEW/SCREENING NOTES ---
{{consultantNotes}}

--- CANDIDATE PROFILE DATA ---
{{signalhireData}}

--- CANDIDATE CV (UPLOADED) ---
{{candidateCV}}

Output the CV in EXACTLY this Markdown structure. If a section has no data, omit it entirely:

| | | |
|---|---|---|
| **Name** | : | **{{candidateName}}** |
| **Position** | : | **{{currentPosition}}** |
| **Company** | : | **{{currentCompany}}** |
| Languages | : | [Languages with proficiency] |
| Notice Period | : | [Notice period if known] |
| Current Salary | : | [Current salary if known] |
| Expected Salary | : | [Expected salary if known] |

## Summary
[2-4 sentence professional summary highlighting key skills relevant to the JD. NEVER mention the target client company name — use generic terms like "a high-growth startup" or "a leading multinational".]

## Educations
**[University Name]**, [Location]
[Degree] | [Year]

## Certifications & Training
- [Certification name]

## Work Experience
**[Company Name]**, [Location]
**[Job Title]** | [Start Date] – [End Date or Present]
- [Achievement bullet point with action verb, quantified where possible]
- [3-5 bullets per role]

**[Previous Company Name]**, [Location]
**[Job Title]** | [Start Date] – [End Date]
- [Achievement bullet points]

IMPORTANT:
- List work experience in reverse chronological order
- Include ALL career stops from the CV but heavily highlight roles relevant to the JD
- Quantify achievements where data is available
- Do NOT include a consultant notes section
- The info table at the top must use the exact "| Key | : | Value |" markdown table format
- Only include info table rows where data is actually available
- The **Name** row MUST contain the candidate's real full name (provided as the candidate name above). If an uploaded CV shows a fuller legal name, prefer that. NEVER leave the Name blank or as a "[Full Name]" placeholder.
- The **Position** and **Company** rows are the candidate's CURRENT (most recent) job title and employer — NOT the role/company being hired for. Use the provided current position/company values; if a value is "Not provided", infer it from the most recent Work Experience entry, and omit the row only if it is genuinely unknown.
- For each Education entry, the university line and the degree-year line MUST be on two separate lines (use a single newline between them, not a space)
- For each Work Experience entry, the company line and the job-title line MUST be on two separate lines, and the job title must be wrapped in **bold**
- Section header for work history must be exactly "Work Experience" (singular)`,
    BLIND_CV_TEMPLATE: `Please convert this candidate CV into our standardised blind CV format for our client {{clientName}} for the role of {{roleName}}.

Anonymize all identifying information so the recipient cannot reverse engineer the candidate's identity.

--- JOB DESCRIPTION / CLIENT INFO ---
{{jobDescription}}

--- CONSULTANT INTERVIEW/SCREENING NOTES ---
{{consultantNotes}}

--- CANDIDATE PROFILE DATA ---
{{signalhireData}}

--- CANDIDATE CV (UPLOADED) ---
{{candidateCV}}

Output the blind CV in EXACTLY this Markdown structure. If a section has no data, omit it entirely:

| | | |
|---|---|---|
| **Name** | : | **[Generated alias code, e.g., FIBT1919 — 4 random uppercase letters + 4 random digits]** |
| **Position** | : | **{{currentPosition}}** |
| **Company** | : | **[Generic, non-identifying description of the candidate's CURRENT employer, e.g., "A leading regional fintech"]** |
| Languages | : | [Languages with proficiency] |
| Notice Period | : | [Notice period if known] |
| Current Salary | : | [Current salary if known] |
| Expected Salary | : | [Expected salary if known] |

## Summary
[2-4 sentence professional summary. Do NOT mention any real company names, university names, or identifying details.]

## Educations
**[Generic University Description]**, [City only]
[Degree with Honours if applicable] | [Year]

## Certifications & Training
- [Certification name — keep generic if identifying]

## Work Experience
**[Generic Company Description]**, [Country]
**[Job Title]** | [Start Date] – [End Date or Present]
- [Achievement bullet point with action verb, quantified where possible]
- [3-5 bullets per role]

**[Generic Company Description]**, [Country]
**[Job Title]** | [Start Date] – [End Date]
- [Achievement bullet points]

IMPORTANT:
- The FIRST cell value in the Name row is the blind alias — this MUST be on the very first line
- The **Position** row is the candidate's CURRENT job title (provided above). The **Company** row MUST be a generic, non-identifying description of their CURRENT employer — NEVER the real company name.
- Replace ALL company names with generic descriptions (e.g., "Major E-commerce & Logistics Platform", "Top-Tier Global Management Consulting Firm (MBB)")
- Replace ALL university names with generic descriptions (e.g., "Top-Ranked Australian University")
- Remove all phone numbers, emails, LinkedIn URLs
- List work experience in reverse chronological order
- Include ALL career stops but highlight roles relevant to the JD
- If details are sparse for a role, use your knowledge of the company/industry to fill in reasonable achievements without exaggeration
- The info table must use the exact "| Key | : | Value |" markdown table format
- Only include info table rows where data is actually available
- Do NOT include a consultant notes section
- For each Education entry, the university line and the degree-year line MUST be on two separate lines (use a single newline between them, not a space)
- For each Work Experience entry, the company line and the job-title line MUST be on two separate lines, and the job title must be wrapped in **bold**
- Section header for work history must be exactly "Work Experience" (singular)`,
    CONSULTANT_NOTES_TEMPLATE: `Based on the following candidate information and job description, generate Consultant Notes for {{candidateName}} for the {{roleName}} role.

--- JOB DESCRIPTION ---
{{jobDescription}}

--- INTERVIEW NOTES ---
{{interviewNotes}}

--- CANDIDATE PROFILE DATA ---
{{signalhireData}}

--- CANDIDATE CV ---
{{candidateCV}}

Output the consultant notes in EXACTLY this Markdown structure. If a section has no data, omit it entirely:

## Summary
[2-4 sentence professional summary of the candidate, highlighting years of experience, key competencies, and why they are a strong fit for the role. Include specific metrics and achievements where available.]

## Consultant Notes - Key Selling Points for {{candidateName}} for {{roleName}}:
- [Key selling point 1 — map directly to a JD requirement with specific evidence from the candidate's background]
- [Key selling point 2]
- [Key selling point 3]
- [Key selling point 4]
- [Key selling point 5]
- [Availability: Notice period information if known]
- [Compensation: Current salary and expectations if known]

[1-2 sentence closing paragraph summarizing why this candidate is a strong overall fit, highlighting the unique combination of skills they bring.]

IMPORTANT:
- Each bullet point should map to a specific JD requirement with evidence from the candidate
- Use specific numbers, metrics, and facts from the CV/notes — avoid vague statements
- The Summary section is a brief overview, the bullet points are the detailed selling points
- Keep the closing paragraph concise and impactful
- Do NOT invent achievements or metrics not present in the source data
- If interview notes mention salary, notice period, or availability, include them as bullet points`
};
/**
 * Fill prompt template with variables.
 */
function fillCVPromptTemplate(template, vars) {
    let result = template;
    for (const [key, value] of Object.entries(vars)) {
        result = result.replaceAll(`{{${key}}}`, value || 'Not provided');
    }
    return result;
}
//# sourceMappingURL=cv-prompts.js.map